import React from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { capitalize } from "shared/lib/capitalize"
import { toClassName } from "shared/lib/toClassName"
import { WithClassName } from "shared/types/WithClassName"
import { Button, ButtonVariant } from "shared/ui/buttons/Button"
import { Chip } from "shared/ui/chip/Chip"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import { TeamInvitation } from "entities/team-invitations/types/TeamInvitation"
import { TeamInvitationStatus } from "entities/team-invitations/types/TeamInvitationStatus"

import "../styles/_project-team-invitation-card.scss"

export interface ProjectTeamInvitationCardViewModel {
  teamInvitation: TeamInvitation
  isAccepting?: boolean
  isRejecting?: boolean
  handleNavigateToProject?: () => void
  handleReject?: () => void
  handleAccept?: () => void
}

interface ProjectTeamInvitationCardViewProps
  extends ProjectTeamInvitationCardViewModel,
    WithClassName {}

export const ProjectTeamInvitationCardView = React.memo(
  ({
    teamInvitation,
    className,
    isAccepting = false,
    isRejecting = false,
    handleAccept,
    handleReject,
    handleNavigateToProject
  }: ProjectTeamInvitationCardViewProps) => {
    const { t } = useTranslation()
    const project = teamInvitation.team.projects?.[0]

    return (
      <li
        className={classNames(
          "card",
          "project-team-invitation-card",
          className
        )}
      >
        <div className="project-team-invitation-card__content">
          <div className="project-team-invitation-card__meta-data">
            <div
              className={classNames(
                "project-team-invitation-card__status",
                `project-team-invitation-card__status--${toClassName(
                  teamInvitation.status
                )}`
              )}
            >
              {t(
                `Team.Invitation.Status.Incoming.${capitalize(
                  teamInvitation.status
                )}`
              )}
            </div>

            <h4 className="project-team-invitation-card__title">
              {project?.title || teamInvitation.team?.name}
            </h4>
            {teamInvitation.invitedBy ? (
              <div className="project-team-invitation-card__invited-by">
                <span className="project-team-invitation-card__invited-by-label">{`${t(
                  "Team.Invitation.InvitedBy"
                )}: `}</span>
                <span className="project-team-invitation-card__invited-by-email">
                  {teamInvitation.invitedBy.name ||
                    teamInvitation.invitedBy.email}
                </span>
              </div>
            ) : null}
            {teamInvitation.team.projects?.[0]?.phase?.label ? (
              <div className="project-team-invitation-card__tags">
                <Chip className="chip--small project-team-invitation-card__tag">
                  {project?.phase?.label}
                </Chip>
              </div>
            ) : null}
          </div>

          {teamInvitation.status === TeamInvitationStatus.ACCEPTED &&
          handleNavigateToProject ? (
            <div className="project-team-invitation-card__actions button-group">
              <Button
                variant={ButtonVariant.PRIMARY}
                onClick={handleNavigateToProject}
                className="project-team-invitation-card__action"
                label={t("Project.ToProject")}
              />
            </div>
          ) : null}

          {teamInvitation.status === TeamInvitationStatus.PENDING ? (
            <div className="project-team-invitation-card__actions button-group">
              {handleReject ? (
                <Button
                  variant={ButtonVariant.SECONDARY}
                  onClick={handleReject}
                  className="project-team-invitation-card__action"
                  label={t("General.Reject")}
                  icon={IconName.CLOSE}
                  isLoading={isRejecting}
                  disabled={isAccepting}
                />
              ) : null}
              {handleAccept ? (
                <Button
                  variant={ButtonVariant.PRIMARY}
                  onClick={handleAccept}
                  className="project-team-invitation-card__action"
                  label={t("General.Accept")}
                  icon={IconName.CHECK}
                  isLoading={isAccepting}
                  disabled={isRejecting}
                />
              ) : null}
            </div>
          ) : null}
        </div>
      </li>
    )
  }
)
